import { useState } from "react";
import { Columns3, ChevronUp, ChevronDown, Eye, EyeOff, Plus } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";

/**
 * "Columns" button for the records table: show/hide and reorder columns.
 * Widths are adjusted by dragging the column edges in the table itself.
 * `cols` is the object returned by useColumnPrefs().
 */
export function ColumnsMenu({ cols, onAddColumn }) {
  const shown = cols.visible.length;
  const [open, setOpen] = useState(false);
  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button variant="outline" size="sm" className="h-8 text-xs gap-1.5" data-testid="columns-menu">
          <Columns3 className="w-3.5 h-3.5" /> Columns
          <span className="text-muted-foreground">{shown}/{cols.all.length}</span>
        </Button>
      </PopoverTrigger>
      <PopoverContent align="end" className="w-72 p-0">
        <div className="px-3 py-2 border-b border-border">
          <div className="text-sm font-medium">Table columns</div>
          <div className="text-xs text-muted-foreground">
            Tick to show, arrows to reorder. Drag a column's edge in the table to resize it.
          </div>
        </div>
        <ul className="max-h-80 overflow-y-auto py-1" data-testid="columns-list">
          {cols.all.map((c, i) => (
            <li key={c.key} className="flex items-center gap-1 px-2 py-1 hover:bg-muted/50 group"
              data-testid={`column-item-${c.key}`}>
              <button type="button" onClick={() => cols.toggle(c.key)}
                className={`flex-1 min-w-0 flex items-center gap-2 text-left text-sm ${c.visible ? "" : "text-muted-foreground"}`}
                data-testid={`column-toggle-${c.key}`}>
                {c.visible
                  ? <Eye className="w-3.5 h-3.5 text-primary shrink-0" />
                  : <EyeOff className="w-3.5 h-3.5 shrink-0" />}
                <span className="truncate">{c.label}</span>
              </button>
              <button type="button" disabled={i === 0} onClick={() => cols.move(c.key, -1)}
                className="p-0.5 rounded text-muted-foreground hover:text-foreground disabled:opacity-30"
                aria-label={`Move ${c.label} left`} data-testid={`column-up-${c.key}`}>
                <ChevronUp className="w-3.5 h-3.5" />
              </button>
              <button type="button" disabled={i === cols.all.length - 1} onClick={() => cols.move(c.key, 1)}
                className="p-0.5 rounded text-muted-foreground hover:text-foreground disabled:opacity-30"
                aria-label={`Move ${c.label} right`} data-testid={`column-down-${c.key}`}>
                <ChevronDown className="w-3.5 h-3.5" />
              </button>
            </li>
          ))}
        </ul>
        {onAddColumn && (
          <div className="px-2 py-1.5 border-t border-border">
            <Button variant="ghost" size="sm" className="w-full justify-start h-8 text-sm text-primary"
              onClick={() => { setOpen(false); onAddColumn(); }} data-testid="columns-add">
              <Plus className="w-3.5 h-3.5 mr-1.5" /> Add column
            </Button>
          </div>
        )}
        <div className="flex items-center justify-between px-3 py-2 border-t border-border">
          <Button variant="ghost" size="sm" className="h-7 text-xs" onClick={cols.showAll}
            data-testid="columns-show-all">Show all</Button>
          <Button variant="ghost" size="sm" className="h-7 text-xs" onClick={cols.reset}
            data-testid="columns-reset">Reset to default</Button>
        </div>
      </PopoverContent>
    </Popover>
  );
}
